<template>
  <div
    ref="container"
    class="text-input"
    :class="{
      'text-input--textarea': type === 'textarea',
      'text-input--card': type === 'card',
      'text-input--has-content': !!modelValue || modelValue === 0,
      'text-input--has-icon': iconClass || type === 'dollars' || type === 'email' || type === 'phone' || type === 'password',
      'text-input--has-error-message': !hideError && errorMessage
    }"
  >
    <button
      v-if="type === 'password'"
      class="text-input__button"
      type="button"
      title="Show/Hide Password"
      tabindex="-1"
      @click="togglePasswordVisibility"
    >
      <span
        class="icon far"
        :class="{
          'fa-eye': passwordInputType === 'password',
          'fa-eye-slash': passwordInputType === 'text'
        }"
      />
    </button>

    <input
      v-if="type === 'card'"

      v-mask-input="cardMaskFormat"
      class="text-input__input"
      type="text"
      v-bind="attributes"
      inputmode="numeric"
      v-on="listeners"
    >

    <input
      v-if="type === 'cvv'"

      v-mask-input="cvvMaskFormat"
      class="text-input__input"
      type="text"
      v-bind="attributes"
      inputmode="numeric"
      v-on="listeners"
    >

    <input
      v-else-if="type === 'dollars'"

      class="text-input__input"
      type="number"
      v-bind="attributes"
      inputmode="decimal"
      v-on="listeners"
    >

    <input
      v-else-if="type === 'email'"

      class="text-input__input"
      type="email"
      v-bind="attributes"
      v-on="listeners"
    >

    <input
      v-else-if="type === 'number'"

      class="text-input__input"
      type="number"
      v-bind="attributes"
      inputmode="decimal"
      v-on="listeners"
    >

    <input
      v-else-if="type === 'password'"

      v-mask-input="mask"
      class="text-input__input"
      :type="passwordInputType"
      v-bind="attributes"
      v-on="listeners"
    >

    <input
      v-else-if="type === 'phone'"

      v-mask-input="'phone'"
      class="text-input__input"
      type="tel"
      v-bind="attributes"
      v-on="listeners"
    >

    <input
      v-else-if="type === 'text'"

      v-mask-input="mask"
      class="text-input__input"
      type="text"
      v-bind="attributes"
      v-on="listeners"
    >

    <textarea
      v-else-if="type === 'textarea'"

      v-mask-input="mask"
      class="text-input__input"
      v-bind="attributes"
      v-on="listeners"
    />

    <input
      v-else-if="type === 'text-as-button'"

      v-mask-input="mask"
      class="text-input__input text-input__input--with-cursor-pointer"
      readonly
      v-bind="attributes"
      v-on="listeners"
    >

    <label
      ref="label"
      class="text-input__label"
      :for="attributes.id"
    >
      {{ label }}
      <span
        v-if="showAsOptional && !allValidations.required"
        class="gray regular smaller"
      >
        (Optional)
      </span>
    </label>

    <div
      class="text-input__icon"
      aria-hidden="true"
    >
      <span
        v-if="iconClass"
        :class="['icon', iconClass]"
      />

      <span
        v-else
        class="icon"
        :class="{
          'fa-usd-circle': type === 'dollars',
          'fa-envelope': type === 'email',
          'fa-key': type === 'password',
          'fa-comment-alt': type === 'phone'
        }"
      />
    </div>

    <card-type-icon
      v-if="cardType"
      :card-type="cardType"
      class="text-input__card-icon"
    />

    <input-error
      v-if="!hideError"
      :error="errorMessage"
    />
  </div>
</template>



<script>
  import { inject, computed, watch } from 'vue';
  import { useField } from 'vee-validate';

  import detectCreditCardType from 'credit-card-type';
  import formatUsd from 'format-usd';
  import lowercase from '@/filters/lowercase';



  export default {
    name: 'TextInput',

    props: {
      allowedCharacters: {
        type: String,
        default: null
      },

      allowNegative: {
        type: Boolean,
        default: true
      },

      autocomplete: {
        type: String,
        default: null
      },

      autoWidth: {
        type: Boolean,
        default: false
      },

      disabled: {
        type: Boolean,
        default: false
      },

      disableDirtyForm: {
        type: Boolean,
        default: false
      },

      hideError: {
        type: Boolean,
        default: false
      },

      iconClass: {
        type: String,
        default: null
      },

      id: {
        type: String,
        default: null
      },

      inputmode: {
        type: String,
        default: null
      },

      label: {
        type: String,
        required: true
      },

      mask: {
        type: [String, Object],
        default: null
      },

      name: {
        type: String,
        required: true
      },

      pattern: {
        type: String,
        default: null
      },

      placeholder: {
        type: String,
        default: null
      },

      preventInput: {
        type: Boolean,
        default: false
      },

      selectAllOnFocus: {
        type: Boolean,
        default: false
      },

      showAsOptional: {
        type: Boolean,
        default: false
      },

      spellcheck: {
        type: Boolean,
        default: true
      },

      type: {
        type: String,
        required: true
      },

      dataThreeDs: {
        type: String,
        default: null
      },

      validations: {
        type: Object,
        default() {
          return {};
        }
      },

      modelValue: {
        type: [String, Number],
        default: null
      }
    },

    // VeeValidate 4 requires the Composition API to register a field with the parent form context.
    // setup() is used exclusively for that purpose — all other component logic
    // remains in the Options API below.
    // Exposes: errorMessage (reactive validation error string, undefined when valid),
    //          allValidations (computed rule set, used in template to pass to the native input)
    // Note: handleChange is used internally by the watch() below and is not exposed.
    setup(props) {
      const formFieldset = inject('formFieldset', {});

      const rules = computed(() => {
        if (formFieldset.isDisabled) {
          return {};
        }

        const defaults = {};

        if (props.type === 'card') {
          defaults.card_type = true;
          defaults.credit_card = true;
        }

        else if (props.type === 'email') {
          defaults.email = true;
        }

        else if (props.type === 'phone') {
          defaults.regex = /^(\([0-9]{3}\))\s[0-9]{3}-[0-9]{4}$/;
        }

        return Object.assign(defaults, props.validations);
      });

      const { errorMessage, handleChange } = useField(() => props.name, rules, {
        initialValue: props.modelValue,
        label: props.label
      });

      watch(() => props.modelValue, handleChange);

      return { errorMessage, allValidations: rules };
    },

    data() {
      return {
        internalValue: null,
        isFocused: false,
        passwordInputType: 'password'
      };
    },

    computed: {
      attributes() {
        return {
          autocomplete: this.autocomplete,
          'data-disable-dirty-form': this.disableDirtyForm,
          'data-vv-as': lowercase(this.label, true),
          disabled: this.disabled,
          id: this.id || this.name,
          inputmode: this.inputmode,
          name: this.name,
          pattern: this.pattern,
          placeholder: this.placeholder,
          spellcheck: this.spellcheck,
          value: this.internalValue,
          /** Paay 3ds provider requires data-threeds attributes */
          ...this.dataThreeDs && { 'data-threeds': this.dataThreeDs }
        };
      },

      cardType() {
        const allowedCardTypes = ['visa', 'american-express', 'discover', 'mastercard', 'jcb', 'diners-club'];

        if (this.type === 'card' && this.modelValue && this.modelValue.length > 1) {
          const cardType = detectCreditCardType(this.modelValue)[0];

          if (cardType && allowedCardTypes.includes(cardType.type)) {
            return cardType.type;
          }

          return null;
        }

        return null;
      },

      cardMaskFormat() {
        const { cardType } = this;

        const format = {
          segments: [19],
          separator: ' '
        };

        if (cardType) {
          if (cardType === 'visa' || cardType === 'mastercard' || cardType === 'discover' || cardType === 'jcb') {
            format.segments = [4, 4, 4, 4];
          }

          else if (cardType === 'american-express') {
            format.segments = [4, 6, 5];
          }

          else if (cardType === 'diners-club') {
            format.segments = [4, 6, 4];
          }
        }

        return format;
      },

      cvvMaskFormat() {
        const { cardNumber } = this.validations.cvv;
        const cardType = detectCreditCardType(cardNumber)[0];
        const format = {
          segments: [4],
          separator: ''
        };

        if (cardNumber && cardType && cardType.type !== 'american-express') {
          format.segments = [3];
        }

        return format;
      },

      listeners() {
        return {
          ...this.$attrs,
          blur: this.blurHandler,
          focus: this.focusHandler,
          input: this.inputHandler,
          keydown: this.keydownHandler
        };
      }
    },

    watch: {
      modelValue: 'updateInternalValue'
    },

    created() {
      this.createdHandler();
    },

    mounted() {
      this.mountedHandler();
    },

    methods: {
      blurHandler(event) {
        this.isFocused = false;

        /*
          NOTE: If a number isn't formatted correctly in the
          input (for examle: "20.2.2" or "1-2.4"), the browser
          will return an empty string as the input value. On
          blur, we'll reset all empty string values to null to
          remove potentially bad input.
        */

        if (event.target.value === '') {
          event.target.value = '';
        }

        this.updateInternalValue(this.modelValue);

        this.$emit('blur', event);
      },

      createdHandler() {
        this.updateInternalValue(this.modelValue);
      },

      focus() {
        this.$el.querySelector('input').focus();
      },

      focusHandler(event) {
        this.isFocused = true;

        if (this.selectAllOnFocus) {
          event.target.select();
          event.target.scrollLeft = 0;
        }

        this.$emit('focus', event);
      },

      inputHandler(event) {
        let { value } = event.target;

        if (this.preventInput) {
          event.preventDefault();
          return;
        }

        if (this.allowedCharacters) {
          const regex = new RegExp(`[^${this.allowedCharacters}]`, 'g');
          value = value.replace(regex, '');
        }

        if (!this.allowNegative) {
          value = value.replace(/-/g, '');
        }

        if (this.type === 'dollars') {
          const hasCents = value.indexOf('.') > -1;
          const dollars = value.replace(/\..*/, '');
          const cents = hasCents ? value.replace(/^[0-9]*\./, '').substr(0, 2) : '';

          if (cents) {
            value = `${dollars}.${cents}`;
          }
        }

        this.internalValue = value;

        if (event.target.type === 'number') {
          if (value) {
            value = Number(value);
          }

          else {
            value = null;
          }
        }

        this.$emit('update:modelValue', value);
      },

      keydownHandler(event) {
        if (!this.allowNegative && event.key === '-') {
          event.preventDefault();
        }

        this.$emit('keydown', event);
      },

      mountedHandler() {
        if (this.autoWidth) {
          const width = Math.max(120, this.$refs.label.clientWidth);
          this.$refs.container.style.width = `${width}px`;
        }
      },

      togglePasswordVisibility() {
        this.passwordInputType = this.passwordInputType === 'password' ? 'text' : 'password';
      },

      updateInternalValue(modelValue) {
        const modelValueIsDefined = !!modelValue || modelValue === 0;
        let { internalValue } = this;

        if (this.type === 'dollars') {
          internalValue = Number(this.internalValue);
        }

        const shouldUpdateInternalValue = !this.isFocused || (modelValue !== internalValue);

        if (shouldUpdateInternalValue) {
          if (modelValueIsDefined && this.type === 'dollars') {
            const dollars = formatUsd({
              amount: modelValue
            }).replace(/[$,]/g, '');

            this.internalValue = dollars;
          }

          else {
            this.internalValue = modelValue;
          }
        }
      }
    }
  };
</script>
